import { useState, useEffect } from 'react'
import { supabase } from './lib/supabaseClient'
import imageCompression from 'browser-image-compression'
import WhatsAppButton from './components/WhatsAppButton'
import QRCodeGenerator from './components/QRCodeGenerator'
import MessagingSystem from './components/MessagingSystem'
import PortalLayout from './components/PortalLayout'
import { responsiveStyles } from './styles/responsiveStyles'

function AgentPortal({ session, onLogout }) {
  const [activeTab, setActiveTab] = useState('properties')
  const [companyId, setCompanyId] = useState(null)
  const [companyName, setCompanyName] = useState('')
  const [properties, setProperties] = useState([])
  const [leads, setLeads] = useState([])
  const [clients, setClients] = useState([])
  const [appointments, setAppointments] = useState([])
  const [emails, setEmails] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [formData, setFormData] = useState({})
  const [uploading, setUploading] = useState(false)
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [emailCompose, setEmailCompose] = useState({ to: '', subject: '', body: '' })
  const [selectedClient, setSelectedClient] = useState(null)
  const [selectedProperty, setSelectedProperty] = useState(null)
  const [showFullImage, setShowFullImage] = useState(null)
  const [showMessages, setShowMessages] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const [userRole] = useState('agent')
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')

  const tabs = [
    { id: 'properties', label: 'Properties', icon: '🏠' },
    { id: 'leads', label: 'Leads', icon: '🎯' },
    { id: 'clients', label: 'Clients', icon: '👤' },
    { id: 'appointments', label: 'Appointments', icon: '📅' },
    { id: 'emails', label: 'Emails', icon: '📧' },
    { id: 'whatsapp', label: 'WhatsApp', icon: '📱' }
  ]

  const usdCountries = ['United States', 'El Salvador', 'Ecuador', 'Panama', 'Zimbabwe', 'Cambodia', 'East Timor', 'South Sudan', 'Somalia', 'Sierra Leone', 'Liberia']
  const eurCountries = ['Germany', 'France', 'Italy', 'Spain', 'Netherlands', 'Belgium', 'Portugal', 'Greece', 'Austria', 'Ireland', 'Finland', 'Slovakia', 'Slovenia', 'Estonia', 'Latvia', 'Lithuania', 'Luxembourg', 'Malta', 'Cyprus', 'Croatia', 'Réunion', 'Mayotte']
  const ngnCountries = ['Nigeria']
  const currencies = ['USD', 'EUR', 'NGN']

  const africanCountries = [
    'Nigeria', 'Ghana', 'Kenya', 'South Africa', 'Egypt', 'Morocco', 'Algeria',
    'Tunisia', 'Libya', 'Sudan', 'Ethiopia', 'Somalia', 'Uganda', 'Tanzania',
    'Rwanda', 'Burundi', 'Djibouti', 'Eritrea', 'Zambia', 'Zimbabwe', 'Botswana',
    'Namibia', 'Mozambique', 'Malawi', 'Angola', 'Congo', 'DR Congo', 'Cameroon',
    'Gabon', 'Equatorial Guinea', 'Chad', 'Niger', 'Mali', 'Burkina Faso', 'Senegal',
    'Gambia', 'Guinea', 'Guinea-Bissau', 'Sierra Leone', 'Liberia', 'Ivory Coast',
    'Benin', 'Togo', 'Mauritania', 'Cape Verde', 'Comoros', 'Mauritius', 'Seychelles',
    'Lesotho', 'Eswatini', 'Madagascar', 'Central African Republic'
  ]

  // Get company ID from session
  useEffect(() => {
    const getCompanyId = async () => {
      if (!session?.user?.id) return

      const { data: userData, error } = await supabase
        .from('users')
        .select('company_id')
        .eq('id', session.user.id)
        .maybeSingle()

      if (error) {
        console.error('Error fetching company_id:', error)
        return
      }

      if (!userData?.company_id) {
        console.log('No company_id found')
        return
      }

      setCompanyId(userData.company_id)
      
      const { data: company } = await supabase
        .from('companies')
        .select('name')
        .eq('id', userData.company_id)
        .maybeSingle()
      
      if (company) {
        setCompanyName(company.name)
      }
      
      setLoading(false)
    }
    
    getCompanyId()
  }, [session])

  const fetchUnreadMessages = async () => {
    if (!companyId) return
    const { count } = await supabase
      .from('messages')
      .select('*', { count: 'exact', head: true })
      .neq('sender_id', session.user.id)
      .eq('is_read', false)
      .eq('company_id', companyId)
    
    setUnreadCount(count || 0)
  }

  useEffect(() => {
    if (companyId) {
      fetchData()
      fetchUnreadMessages()
    }
    
    const interval = setInterval(fetchUnreadMessages, 30000)
    return () => clearInterval(interval)
  }, [activeTab, companyId])

  const fetchData = async () => {
    if (!companyId) return
    setLoading(true)
    
    try {
      if (activeTab === 'properties') {
        const { data } = await supabase
          .from('properties')
          .select('*')
          .eq('company_id', companyId)
          .eq('agent_id', session.user.id)
          .order('created_at', { ascending: false })
        setProperties(data || [])
      } else if (activeTab === 'leads') {
        const { data } = await supabase
          .from('leads')
          .select('*, properties!property_id(title)')
          .eq('company_id', companyId)
          .eq('agent_id', session.user.id)
          .order('created_at', { ascending: false })
        setLeads(data || [])
      } else if (activeTab === 'clients') {
        const { data } = await supabase
          .from('clients')
          .select('*')
          .eq('company_id', companyId)
          .eq('agent_id', session.user.id)
          .order('created_at', { ascending: false })
        setClients(data || [])
      } else if (activeTab === 'appointments') {
        const { data } = await supabase
          .from('appointments')
          .select('*, clients(full_name)')
          .eq('company_id', companyId)
          .eq('agent_id', session.user.id)
          .order('appointment_date', { ascending: true })
        setAppointments(data || [])
      } else if (activeTab === 'emails') {
        const { data } = await supabase
          .from('emails')
          .select('*')
          .eq('company_id', companyId)
          .eq('agent_id', session.user.id)
          .order('created_at', { ascending: false })
        setEmails(data || [])
      }
    } catch (err) {
      console.error('Error fetching data:', err)
    }
    setLoading(false)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      if (activeTab === 'properties') {
        const propertyData = { ...formData, agent_id: session.user.id, company_id: companyId, images: formData.images || [] }
        if (editingItem) {
          await supabase.from('properties').update(propertyData).eq('id', editingItem.id)
        } else {
          await supabase.from('properties').insert(propertyData)
        }
      } else if (activeTab === 'leads') {
  const leadData = {
    client_name: formData.client_name || '',
    client_email: formData.client_email || null,
    client_phone: formData.client_phone || null,
    status: formData.status || 'new',
    notes: formData.notes || null,
    score: formData.score || 10,
    agent_id: session.user.id,
    company_id: companyId
  }
  if (editingItem) {
    await supabase.from('leads').update(leadData).eq('id', editingItem.id)
  } else {
    await supabase.from('leads').insert(leadData)
  }
      } else if (activeTab === 'clients') {
        const clientData = { ...formData, agent_id: session.user.id, company_id: companyId }
        if (editingItem) {
          await supabase.from('clients').update(clientData).eq('id', editingItem.id)
        } else {
          await supabase.from('clients').insert(clientData)
        }
     } else if (activeTab === 'appointments') {
  const appointmentData = {
    client_id: formData.client_id || null,
    agent_id: session.user.id,
    company_id: companyId,
    appointment_date: formData.appointment_date
      ? new Date(formData.appointment_date).toISOString()
      : null,
    appointment_type: formData.appointment_type || null,
    location: formData.location || null,
    meeting_link: formData.meeting_link || null,
    notes: formData.notes || null,
    status: formData.status || 'scheduled'
  }
  if (editingItem) {
    await supabase.from('appointments').update(appointmentData).eq('id', editingItem.id)
  } else {
    await supabase.from('appointments').insert(appointmentData)
  }
}

      setShowModal(false)
      setEditingItem(null)
      setFormData({})
      fetchData()
    } catch (err) {
      alert('Error: ' + err.message)
    }
    setLoading(false)
  }

  const handleDelete = async (id) => {
    if (confirm('Are you sure?')) {
      let table = ''
      if (activeTab === 'properties') table = 'properties'
      else if (activeTab === 'leads') table = 'leads'
      else if (activeTab === 'clients') table = 'clients'
      else if (activeTab === 'appointments') table = 'appointments'
      
      await supabase.from(table).delete().eq('id', id)
      fetchData()
    }
  }

  const compressImage = async (file) => {
    const options = {
      maxSizeMB: 0.5,
      maxWidthOrHeight: 1200,
      useWebWorker: true
    }
    try {
      const compressedFile = await imageCompression(file, options)
      return compressedFile
    } catch (error) {
      console.error('Compression error:', error)
      return file
    }
  }

  const handlePropertyImageUpload = async (propertyId, file) => {
    if (!file) {
      alert('Please select a file')
      return
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg']
    if (!allowedTypes.includes(file.type)) {
      alert('Only JPEG, PNG, and WebP images are allowed')
      return
    }

    const maxSize = 2 * 1024 * 1024
    if (file.size > maxSize) {
      alert(`File too large! Please upload an image smaller than 2MB.`)
      return
    }

    setUploading(true)

    const fileExt = file.name.split('.').pop()
    const fileName = `property_${propertyId}_${Date.now()}.${fileExt}`
    const filePath = fileName

    const { error: uploadError } = await supabase.storage
      .from('property-images')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true
      })

    if (uploadError) {
      console.error('Upload error:', uploadError)
      alert('Upload failed: ' + uploadError.message)
      setUploading(false)
      return
    }

    const { data: { publicUrl } } = supabase.storage
      .from('property-images')
      .getPublicUrl(filePath)

    const property = properties.find(p => p.id === propertyId)
    const currentImages = property?.images || []
    
    if (currentImages.length >= 10) {
      alert('Maximum 10 images per property.')
      setUploading(false)
      return
    }
    
    const { error: updateError } = await supabase
      .from('properties')
      .update({ images: [...currentImages, publicUrl] })
      .eq('id', propertyId)

    if (updateError) {
      console.error('Update error:', updateError)
      alert('Failed to update property: ' + updateError.message)
    } else {
      alert('Image uploaded successfully!')
      fetchData()
    }
    setUploading(false)
  }

  const handleDeleteImage = async (propertyId, imageUrl) => {
    if (confirm('Remove this image?')) {
      const property = properties.find(p => p.id === propertyId)
      const updatedImages = property.images.filter(img => img !== imageUrl)
      await supabase.from('properties').update({ images: updatedImages }).eq('id', propertyId)
      fetchData()
    }
  }

  const sendEmail = async () => {
    if (!emailCompose.to || !emailCompose.body) {
      alert('Please fill in recipient and message')
      return
    }

    const { error } = await supabase.from('emails').insert({
      to_email: emailCompose.to,
      subject: emailCompose.subject || 'No Subject',
      body: emailCompose.body,
      from_email: session.user.email,
      direction: 'outbound',
      agent_id: session.user.id,
      client_id: selectedClient?.id || null,
      company_id: companyId
    })

    if (error) {
      alert('Failed to send: ' + error.message)
    } else {
      alert('Email sent successfully!')
      setEmailCompose({ to: '', subject: '', body: '' })
      setShowEmailModal(false)
      fetchData()
    }
  }

  const updateLeadScore = async (leadId, newStatus) => {
    const scoreMap = { new: 10, contacted: 30, negotiating: 70, closed: 100 }
    await supabase.from('leads').update({ status: newStatus, score: scoreMap[newStatus] }).eq('id', leadId)
    fetchData()
  }

  const getLeadScoreColor = (score) => {
    if (score >= 70) return '#059669'
    if (score >= 30) return '#d97706'
    return '#6b7280'
  }

  const getStatusBadge = (status) => {
    const badges = {
      'new': { color: '#6b7280', label: '🟡 New' },
      'contacted': { color: '#2c7da0', label: '📞 Contacted' },
      'negotiating': { color: '#d97706', label: '🤝 Negotiating' },
      'closed': { color: '#059669', label: '✅ Closed' }
    }
    return badges[status] || badges.new
  }

  // ===== RENDER FORM =====
  const renderForm = () => {
    const inputStyle = { width: '100%', padding: 12, margin: '8px 0', border: '1px solid #cbd5e1', borderRadius: 12, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }
    
    if (activeTab === 'properties') {
      return (
        <>
          <input placeholder="Property Title" value={formData.title || ''} onChange={e => setFormData({...formData, title: e.target.value})} required style={inputStyle} />
          <textarea placeholder="Description" rows="3" value={formData.description || ''} onChange={e => setFormData({...formData, description: e.target.value})} style={inputStyle} />
          <input placeholder="Price" type="number" value={formData.price || ''} onChange={e => setFormData({...formData, price: parseFloat(e.target.value)})} style={inputStyle} />
          <select value={formData.currency || 'USD'} onChange={e => setFormData({...formData, currency: e.target.value})} style={inputStyle}>
            {currencies.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={formData.country || ''} onChange={e => setFormData({...formData, country: e.target.value})} style={inputStyle}>
            <option value="">Select Country</option>
            <optgroup label="🌍 African Countries">
              {africanCountries.map(c => <option key={c} value={c}>{c}</option>)}
            </optgroup>
            <optgroup label="🇺🇸 USD Countries">
              {usdCountries.map(c => <option key={c} value={c}>{c}</option>)}
            </optgroup>
            <optgroup label="🇪🇺 EUR Countries">
              {eurCountries.map(c => <option key={c} value={c}>{c}</option>)}
            </optgroup>
          </select>
          <input placeholder="City" value={formData.city || ''} onChange={e => setFormData({...formData, city: e.target.value})} style={inputStyle} />
          <input placeholder="Address" value={formData.address || ''} onChange={e => setFormData({...formData, address: e.target.value})} style={inputStyle} />
          <input placeholder="Bedrooms" type="number" value={formData.bedrooms || ''} onChange={e => setFormData({...formData, bedrooms: parseInt(e.target.value)})} style={inputStyle} />
          <input placeholder="Bathrooms" type="number" value={formData.bathrooms || ''} onChange={e => setFormData({...formData, bathrooms: parseInt(e.target.value)})} style={inputStyle} />
        </>
      )
    }
    if (activeTab === 'leads') {
      return (
        <>
          <input placeholder="Client Name" value={formData.client_name || ''} onChange={e => setFormData({...formData, client_name: e.target.value})} required style={inputStyle} />
          <input placeholder="Email" type="email" value={formData.client_email || ''} onChange={e => setFormData({...formData, client_email: e.target.value})} style={inputStyle} />
          <input placeholder="Phone" value={formData.client_phone || ''} onChange={e => setFormData({...formData, client_phone: e.target.value})} style={inputStyle} />
          <select value={formData.status || 'new'} onChange={e => setFormData({...formData, status: e.target.value})} style={inputStyle}>
            <option value="new">🟡 New (10 pts)</option>
            <option value="contacted">📞 Contacted (30 pts)</option>
            <option value="negotiating">🤝 Negotiating (70 pts)</option>
            <option value="closed">✅ Closed (100 pts)</option>
          </select>
          <textarea placeholder="Notes" rows="3" value={formData.notes || ''} onChange={e => setFormData({...formData, notes: e.target.value})} style={inputStyle} />
        </>
      )
    }
    if (activeTab === 'clients') {
      return (
        <>
          <input placeholder="Full Name" value={formData.full_name || ''} onChange={e => setFormData({...formData, full_name: e.target.value})} required style={inputStyle} />
          <input placeholder="Email" type="email" value={formData.email || ''} onChange={e => setFormData({...formData, email: e.target.value})} style={inputStyle} />
          <input placeholder="Phone" value={formData.phone || ''} onChange={e => setFormData({...formData, phone: e.target.value})} style={inputStyle} />
          <input placeholder="Address" value={formData.address || ''} onChange={e => setFormData({...formData, address: e.target.value})} style={inputStyle} />
        </>
      )
    }
    if (activeTab === 'appointments') {
      return (
        <>
          <select value={formData.client_id || ''} onChange={e => setFormData({...formData, client_id: e.target.value})} required style={inputStyle}>
            <option value="">Select Client</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
          </select>
          <input type="datetime-local" value={formData.appointment_date || ''} onChange={e => setFormData({...formData, appointment_date: e.target.value})} required style={inputStyle} />
          <input placeholder="Location" value={formData.location || ''} onChange={e => setFormData({...formData, location: e.target.value})} style={inputStyle} />
          <textarea placeholder="Notes" rows="3" value={formData.notes || ''} onChange={e => setFormData({...formData, notes: e.target.value})} style={inputStyle} />
          <select value={formData.status || 'scheduled'} onChange={e => setFormData({...formData, status: e.target.value})} style={inputStyle}>
            <option value="scheduled">📅 Scheduled</option>
            <option value="completed">✅ Completed</option>
            <option value="cancelled">❌ Cancelled</option>
          </select>
        </>
      )
    }
    return null
  }

  // ===== RENDER LIST =====
  const renderList = () => {
    if (loading) return <div style={{ textAlign: 'center', padding: 40 }}>⏳ Loading...</div>

    const cardStyle = { background: '#fff', borderRadius: 20, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #e2e8f0' }
    const editBtn = { background: '#2c7da0', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 8, marginRight: 10, cursor: 'pointer', fontSize: 13 }
    const deleteBtn = { background: '#dc2626', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }
    const emailBtn = { background: '#059669', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 8, marginRight: 10, cursor: 'pointer', fontSize: 13 }
    const galleryContainer = { marginTop: 15 }
    const imageGrid = { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }
    const imageWrapper = { position: 'relative', width: 60, height: 60 }
    const thumbnailStyle = { width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8, cursor: 'pointer' }
    const removeImageBtn = { position: 'absolute', top: -6, right: -6, background: '#dc2626', color: '#fff', border: 'none', borderRadius: '50%', width: 18, height: 18, cursor: 'pointer', fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }
    const addImageBtn = { width: 60, height: 60, border: '2px dashed #cbd5e1', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: '#f8fafc' }
    const addImageContent = { fontSize: 12, color: '#64748b' }

    // ===== PROPERTIES =====
    if (activeTab === 'properties') {
      if (properties.length === 0) return <p style={{ textAlign: 'center', padding: 40 }}>No properties yet. Click "+ Add New" to create one.</p>
      return (
        <div style={responsiveStyles.grid}>
          {properties.map(p => (
            <div key={p.id} style={cardStyle}>
              <h3>{p.title}</h3>
              <p style={{ color: '#64748b' }}>{p.description?.substring(0, 100)}...</p>
              <p style={{ fontSize: 20, fontWeight: 700, color: '#1e3a5f', margin: '10px 0' }}>{p.currency} {p.price?.toLocaleString()}</p>
              <p>📍 {p.country}, {p.city}</p>
              <p>🛏️ {p.bedrooms} beds • 🛁 {p.bathrooms} baths</p>
              
              <div style={galleryContainer}>
                <div style={imageGrid}>
                  {p.images && p.images.length > 0 && p.images.map((img, idx) => (
                    <div key={idx} style={imageWrapper}>
                      <img 
                        src={img} 
                        alt={`${p.title} ${idx + 1}`} 
                        style={thumbnailStyle}
                        onClick={() => setShowFullImage(img)}
                      />
                      <button 
                        onClick={() => handleDeleteImage(p.id, img)}
                        style={removeImageBtn}
                        title="Remove image"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <label style={addImageBtn}>
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={(e) => handlePropertyImageUpload(p.id, e.target.files[0])}
                      disabled={uploading}
                      style={{ display: 'none' }}
                    />
                    <div style={addImageContent}>
                      {uploading ? '⏳' : '+ Add'}
                    </div>
                  </label>
                </div>
              </div>

              <div style={{ marginTop: 15, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={() => { setEditingItem(p); setFormData(p); setShowModal(true) }} style={editBtn}>Edit</button>
                <button onClick={() => handleDelete(p.id)} style={deleteBtn}>Delete</button>
                <WhatsAppButton type="property" data={p} phoneNumber="" session={session} companyName={companyName} />
                <QRCodeGenerator property={p} />
              </div>
            </div>
          ))}
        </div>
      )
    }

    // ===== LEADS =====
    if (activeTab === 'leads') {
      if (leads.length === 0) return <p style={{ textAlign: 'center', padding: 40 }}>No leads yet.</p>
      
      const filteredLeads = leads.filter(l => {
        if (filterStatus === 'all') return true
        return l.status === filterStatus
      })

      return (
        <div>
          <div style={{ marginBottom: 20, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <input 
              type="text" 
              placeholder="🔍 Search leads..." 
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ padding: '10px', border: '1px solid #cbd5e1', borderRadius: 8, flex: 1 }}
            />
            <select 
              value={filterStatus} 
              onChange={e => setFilterStatus(e.target.value)}
              style={{ padding: '10px', border: '1px solid #cbd5e1', borderRadius: 8 }}
            >
              <option value="all">All Status</option>
              <option value="new">🟡 New</option>
              <option value="contacted">📞 Contacted</option>
              <option value="negotiating">🤝 Negotiating</option>
              <option value="closed">✅ Closed</option>
            </select>
          </div>
          <div style={responsiveStyles.grid}>
            {filteredLeads.map(l => {
              const scoreColor = getLeadScoreColor(l.score || 10)
              const statusBadge = getStatusBadge(l.status)
              return (
                <div key={l.id} style={cardStyle}>
                  <h3>{l.client_name}</h3>
                  <p>📧 {l.client_email} | 📞 {l.client_phone}</p>
                  <p>Score: <span style={{ background: scoreColor, color: '#fff', padding: '2px 8px', borderRadius: 20 }}>{l.score || 10} pts</span></p>
                  <p>Status: 
                    <select 
                      value={l.status} 
                      onChange={(e) => updateLeadScore(l.id, e.target.value)} 
                      style={{ marginLeft: 10, padding: 5, borderRadius: 8, border: '1px solid #cbd5e1' }}
                    >
                      <option value="new">🟡 New</option>
                      <option value="contacted">📞 Contacted</option>
                      <option value="negotiating">🤝 Negotiating</option>
                      <option value="closed">✅ Closed</option>
                    </select>
                  </p>
                  <p>🏠 Property: {l.properties?.title || 'Not assigned'}</p>
                  {l.notes && <p style={{ color: '#64748b', fontSize: 13 }}>📝 {l.notes}</p>}
                  <div style={{ marginTop: 15, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button 
                      onClick={() => { 
                        setSelectedClient({ id: l.id, email: l.client_email, full_name: l.client_name }); 
                        setEmailCompose({ to: l.client_email, subject: '', body: '' }); 
                        setShowEmailModal(true) 
                      }} 
                      style={emailBtn}
                    >
                      📧 Email
                    </button>
                    <button onClick={() => { setEditingItem(l); setFormData(l); setShowModal(true) }} style={editBtn}>Edit</button>
                    <button onClick={() => handleDelete(l.id)} style={deleteBtn}>Delete</button>
                    <WhatsAppButton type="lead" data={l} phoneNumber={l.client_phone} session={session} companyName={companyName} />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )
    }

    // ===== CLIENTS =====
    if (activeTab === 'clients') {
      if (clients.length === 0) return <p style={{ textAlign: 'center', padding: 40 }}>No clients yet.</p>
      
      const filteredClients = clients.filter(c => 
        c.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.email?.toLowerCase().includes(searchTerm.toLowerCase())
      )

      return (
        <div>
          <div style={{ marginBottom: 20 }}>
            <input 
              type="text" 
              placeholder="🔍 Search clients..." 
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ padding: '10px', border: '1px solid #cbd5e1', borderRadius: 8, width: '100%' }}
            />
          </div>
          <div style={responsiveStyles.grid}>
            {filteredClients.map(c => (
              <div key={c.id} style={cardStyle}>
                <h3>{c.full_name}</h3>
                <p>📧 {c.email} | 📞 {c.phone}</p>
                <p>📍 {c.address || 'N/A'}</p>
                <div style={{ marginTop: 15, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button 
                    onClick={() => { 
                      setSelectedClient(c); 
                      setEmailCompose({ to: c.email, subject: '', body: '' }); 
                      setShowEmailModal(true) 
                    }} 
                    style={emailBtn}
                  >
                    📧 Email
                  </button>
                  <button onClick={() => { setEditingItem(c); setFormData(c); setShowModal(true) }} style={editBtn}>Edit</button>
                  <button onClick={() => handleDelete(c.id)} style={deleteBtn}>Delete</button>
                  <WhatsAppButton type="client" data={c} phoneNumber={c.phone} session={session} companyName={companyName} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )
    }

    // ===== APPOINTMENTS =====
    if (activeTab === 'appointments') {
      if (appointments.length === 0) return <p style={{ textAlign: 'center', padding: 40 }}>No appointments.</p>
      return (
        <div style={responsiveStyles.grid}>
          {appointments.map(a => (
            <div key={a.id} style={cardStyle}>
              <h3>📅 {new Date(a.appointment_date).toLocaleString()}</h3>
              <p>👤 Client: {a.clients?.full_name}</p>
              <p>📍 Location: {a.location || 'TBD'}</p>
              <p>Status: <span style={{ 
                background: a.status === 'completed' ? '#d1fae5' : a.status === 'cancelled' ? '#fee2e2' : '#fed7aa', 
                padding: '4px 12px', 
                borderRadius: 20,
                fontSize: 12
              }}>
                {a.status || 'scheduled'}
              </span></p>
              {a.notes && <p style={{ color: '#64748b', fontSize: 13 }}>📝 {a.notes}</p>}
              <div style={{ marginTop: 15, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={() => { setEditingItem(a); setFormData(a); setShowModal(true) }} style={editBtn}>Edit</button>
                <button onClick={() => handleDelete(a.id)} style={deleteBtn}>Delete</button>
                {a.clients && (
                  <WhatsAppButton 
                    type="appointment" 
                    data={{ 
                      ...a, 
                      client_name: a.clients?.full_name,
                      client_phone: a.clients?.phone
                    }} 
                    phoneNumber={a.clients?.phone} 
                    session={session} 
                    companyName={companyName} 
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      )
    }

    // ===== EMAILS =====
    if (activeTab === 'emails') {
      if (emails.length === 0) return <p style={{ textAlign: 'center', padding: 40 }}>No emails yet.</p>
      return (
        <div style={responsiveStyles.grid}>
          {emails.map(e => (
            <div key={e.id} style={cardStyle}>
              <p><strong>{e.direction === 'outbound' ? '📤 Sent' : '📥 Received'}</strong> to: {e.to_email}</p>
              <p><strong>Subject:</strong> {e.subject}</p>
              <p style={{ color: '#64748b' }}>{e.body?.substring(0, 150)}...</p>
              <p style={{ fontSize: 12, color: '#64748b', marginTop: 10 }}>{new Date(e.created_at).toLocaleString()}</p>
            </div>
          ))}
        </div>
      )
    }

    return null
  }

  // ===== STYLES =====
  const addBtn = { background: '#1e3a5f', color: '#fff', border: 'none', padding: '12px 28px', borderRadius: 40, fontSize: 14, fontWeight: 600, cursor: 'pointer' }
  const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30, flexWrap: 'wrap', gap: 15 }
  const modalOverlay = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }
  const modalStyle = { background: '#fff', padding: 30, borderRadius: 24, width: '90%', maxWidth: 550, maxHeight: '85vh', overflow: 'auto' }
  const saveBtn = { background: '#059669', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: 12, cursor: 'pointer', fontWeight: 600 }
  const cancelBtn = { background: '#6b7280', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: 12, cursor: 'pointer' }
  const inputStyle = { width: '100%', padding: 12, margin: '8px 0', border: '1px solid #cbd5e1', borderRadius: 12, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }
  const modalOverlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '20px' }
  const fullImageOverlay = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }
  const fullImageContainer = { position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }
  const fullImageStyle = { maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain', borderRadius: 8 }
  const closeFullBtn = { position: 'absolute', top: -40, right: 0, background: '#fff', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer' }

  if (loading && !companyId) {
    return <div style={{ textAlign: 'center', padding: '50px' }}>Loading company data...</div>
  }

  return (
    <PortalLayout
      session={session}
      onLogout={onLogout}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      tabs={tabs}
      unreadCount={unreadCount}
      portalTitle="Agent Portal"
      portalIcon="🤝"
      onShowMessages={() => setShowMessages(true)}
    >
      {/* Messages Modal */}
      {showMessages && (
        <div style={modalOverlayStyle} onClick={() => setShowMessages(false)}>
          <div onClick={e => e.stopPropagation()}>
            <MessagingSystem 
              currentUser={session.user}
              userRole={userRole}
              onClose={() => setShowMessages(false)}
            />
          </div>
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <div style={modalOverlay} onClick={() => setShowEmailModal(false)}>
          <div style={{...modalStyle, maxWidth: 500}} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginBottom: 20 }}>📧 {selectedClient ? `Send Email to ${selectedClient.full_name}` : 'Compose Email'}</h2>
            <input 
              placeholder="To" 
              value={emailCompose.to} 
              onChange={e => setEmailCompose({...emailCompose, to: e.target.value})} 
              style={inputStyle} 
            />
            <input 
              placeholder="Subject" 
              value={emailCompose.subject} 
              onChange={e => setEmailCompose({...emailCompose, subject: e.target.value})} 
              style={inputStyle} 
            />
            <textarea 
              placeholder="Message" 
              rows="8" 
              value={emailCompose.body} 
              onChange={e => setEmailCompose({...emailCompose, body: e.target.value})} 
              style={inputStyle} 
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button onClick={sendEmail} style={saveBtn}>Send Email</button>
              <button onClick={() => setShowEmailModal(false)} style={cancelBtn}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={headerStyle}>
        <div>
          <h1 style={{ margin: 0 }}>{activeTab === 'whatsapp' ? 'WhatsApp Messaging' : activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}</h1>
          <p style={{ color: '#64748b', marginTop: 4 }}>Welcome back, {session.user.email} - {companyName}</p>
        </div>
        {activeTab !== 'emails' && activeTab !== 'whatsapp' && (
          <button onClick={() => { setEditingItem(null); setFormData({}); setShowModal(true) }} style={addBtn}>
            + Add New
          </button>
        )}
      </div>

      {/* Main Content */}
      <div>{renderList()}</div>

      {/* WhatsApp Tab */}
      {activeTab === 'whatsapp' && (
        <div>
          <h2 style={{ fontSize: '24px', color: '#1e3a5f' }}>📱 WhatsApp Messaging - {companyName}</h2>
          <div style={responsiveStyles.grid}>
            <div style={{ background: '#fff', borderRadius: 20, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #e2e8f0' }}>
              <h3>🏠 Share Property</h3>
              <p>Send property details to potential buyers</p>
              {properties.slice(0, 3).map(property => (
                <div key={property.id} style={{ marginBottom: 10, padding: 10, background: '#f8fafc', borderRadius: 8 }}>
                  <p><strong>{property.title}</strong> - {property.currency} {property.price?.toLocaleString()}</p>
                  <WhatsAppButton type="property" data={property} phoneNumber="" session={session} companyName={companyName} />
                </div>
              ))}
            </div>
            
            <div style={{ background: '#fff', borderRadius: 20, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #e2e8f0' }}>
              <h3>🎯 Contact Leads</h3>
              <p>Send updates to your leads</p>
              {leads.slice(0, 3).map(lead => (
                <div key={lead.id} style={{ marginBottom: 10, padding: 10, background: '#f8fafc', borderRadius: 8 }}>
                  <p><strong>{lead.client_name}</strong> - {lead.status} ({lead.score || 0} pts)</p>
                  <WhatsAppButton type="lead" data={lead} phoneNumber={lead.client_phone} session={session} companyName={companyName} />
                </div>
              ))}
            </div>

            <div style={{ background: '#fff', borderRadius: 20, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #e2e8f0' }}>
              <h3>📅 Appointment Reminders</h3>
              <p>Send reminders for upcoming appointments</p>
              {appointments.filter(a => a.status === 'scheduled').slice(0, 3).map(apt => {
                const client = clients.find(c => c.id === apt.client_id)
                return (
                  <div key={apt.id} style={{ marginBottom: 10, padding: 10, background: '#f8fafc', borderRadius: 8 }}>
                    <p><strong>{client?.full_name || 'Client'}</strong> - {new Date(apt.appointment_date).toLocaleString()}</p>
                    <WhatsAppButton type="appointment" data={apt} phoneNumber={client?.phone} session={session} companyName={companyName} />
                  </div>
                )
              })}
            </div>

            <div style={{ background: '#fff', borderRadius: 20, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #e2e8f0' }}>
              <h3>📢 Bulk Message</h3>
              <p>Send promotional messages to all leads</p>
              <WhatsAppButton 
                type="bulk" 
                data={{ message: `🏠 New properties available at ${companyName}! Check out our latest listings.` }} 
                phoneNumber=""
                session={session}
                buttonText="📢 Message All Leads"
                companyName={companyName}
              />
              <div style={{ marginTop: 10 }}>
                <WhatsAppButton 
                  type="bulk" 
                  data={{ message: `📅 Reminder: ${companyName} has new properties in your area. Schedule a viewing today!` }} 
                  phoneNumber=""
                  session={session}
                  buttonText="🏠 Property Alert"
                  companyName={companyName}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div style={modalOverlay} onClick={() => setShowModal(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginBottom: 20 }}>{editingItem ? '✏️ Edit' : '➕ Add New'} {activeTab.slice(0, -1)}</h2>
            <form onSubmit={handleSubmit}>
              {renderForm()}
              <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                <button type="submit" style={saveBtn}>Save</button>
                <button type="button" onClick={() => { setShowModal(false); setEditingItem(null) }} style={cancelBtn}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Full Image Modal */}
      {showFullImage && (
        <div style={fullImageOverlay} onClick={() => setShowFullImage(null)}>
          <div style={fullImageContainer}>
            <img src={showFullImage} alt="Full size" style={fullImageStyle} />
            <button onClick={() => setShowFullImage(null)} style={closeFullBtn}>✕ Close</button>
          </div>
        </div>
      )}
    </PortalLayout>
  )
}

export default AgentPortal